import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Customer, CustomerConfig } from '../../models/customer.config';
import { BehaviorSubject, EMPTY, map, Observable, of, switchMap } from 'rxjs';
import { GetCustomerConfigGQL } from '../../../generated/graphql';
import { CustomerService } from './customer.service';
import { mapDoorTypeFromGraphQlToIntern } from "../../utils/door-type-mapping";
import { Router } from "@angular/router";
import {DoorType} from "../../models/door";
import {mapSmallGraphQLProductTypeToSmallProductType, SmallProductType} from "../product-type/product-type";

export enum Stage {
  DEV = 'dev',
  TEST = 'test',
  PROD = 'prod',
}

@Injectable({ providedIn: 'root' })
export class CustomerServiceImpl extends CustomerService {

  private customer$ = new BehaviorSubject<Customer | undefined>(undefined);

  constructor(private getCustomerConfigGQL: GetCustomerConfigGQL, private router: Router) {
    super();
  }

  static GetCustomerKeyFromUrl(stage: Stage): string | undefined {
    if (stage === Stage.TEST) {
      const regex = /https:\/\/([a-zA-Z0-9-]+)\.test\.door-vision\.cloud/;
      const match = window.location.href.match(regex);

      if (match && match.length >= 2) {
        return match[1];
      } else {
        return undefined;
      }
    }

    const regex = /https:\/\/([a-zA-Z0-9-]+)\.door-vision\.com/;
    const match = window.location.href.match(regex);

    if (match && match.length >= 2) {
      return match[1];
    } else {
      return undefined;
    }
  }

  static GetCustomerKeyFromUrlParams(): string | undefined {
    const urlParams = new URLSearchParams(window.location.search);
    const customerKeyFromUrl = urlParams.get('c');
    if (customerKeyFromUrl) {
      return customerKeyFromUrl
    } else {
      return undefined
    }
  }

  static GetCustomerKey(): string {
    try {
      const stage = environment.stage

      /* if (stage === Stage.PROD) {
        // To be on the safe side on prod, we always use the customer key from the environment
        return environment.customerKey
      } else */ if (stage === Stage.TEST || stage === Stage.PROD) {

        // 1. Check for customer key in the url query params
        const customerKeyFromUrlParams = CustomerServiceImpl.GetCustomerKeyFromUrlParams()
        if (customerKeyFromUrlParams) {
          return customerKeyFromUrlParams
        }

        // 2. Check for customer key in the url
        const customerKeyFromUrl = CustomerServiceImpl.GetCustomerKeyFromUrl(stage)
        if (customerKeyFromUrl) {
          return customerKeyFromUrl
        }

        // 3. Check for customer key in the environment
        if (environment.customerKey) {
          return environment.customerKey
        }

        console.warn('Could not extract customer key from query params, url or environment. Falling back to demo.')
        return 'demo'
      } else {
        // 1. Check for customer key in the url query params
        const customerKeyFromUrlParams = CustomerServiceImpl.GetCustomerKeyFromUrlParams()
        if (customerKeyFromUrlParams) {
          return customerKeyFromUrlParams
        }

        // 2. Fallback to the environment
        return environment.customerKey
      }
    } catch (e) {
      console.warn('Error during customer key extraction, falling back to environment.customerKey or demo', e)
      return environment.customerKey || 'demo'
    }
  }

  getCustomerConfig(): CustomerConfig | undefined {
    return this.customer$.getValue()?.config;
  }

  getCustomerConfig$(): Observable<CustomerConfig> {
    return this.customer$.pipe(switchMap(value => value ? of(value.config) : EMPTY));
  }

  getCustomerName$(): Observable<string> {
    return this.customer$.pipe(switchMap(value => value ? of(value.name) : EMPTY));
  }

  loadCustomer(): void {
    this.loadCustomer$().subscribe({
      next: customer => {
        this.customer$.next(customer);
      },
      error: err => {
        console.warn('could not load customer', err);
      }
    });
  }

  private hexToRgb(hex: string): string | undefined {
    // Remove any leading '#' character if present
    hex = hex.replace(/^#/, '');

    // Expand short hex strings (3 characters) to full format (6 characters)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return `${r},${g},${b}`
  }

  private calculateTextColor(background: string): string {
    function hexToRgb(hex: string): [number, number, number] {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return [r, g, b];
    }

    function calculateRelativeLuminance(rgb: [number, number, number]): number {
      const [r, g, b] = rgb.map(value => {
        value /= 255;
        return value <= 0.03928
          ? value / 12.92
          : Math.pow((value + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    const black = "#000000"; // Black color
    const white = "#ffffff"; // White color

    const rgbBackground = hexToRgb(background);
    const rgbBlack = hexToRgb(black);
    const rgbWhite = hexToRgb(white);

    const luminanceBackground = calculateRelativeLuminance(rgbBackground);
    const luminanceBlack = calculateRelativeLuminance(rgbBlack);
    const luminanceWhite = calculateRelativeLuminance(rgbWhite);

    const contrastToBlack = (luminanceBackground + 0.05) / (luminanceBlack + 0.05);
    const contrastToWhite = (luminanceBackground + 0.05) / (luminanceWhite + 0.05);

    // Determine the text color based on the contrast ratio
    if (contrastToBlack >= 4.5) {
      return black;
    } else if (contrastToWhite >= 4.5) {
      return white;
    } else {
      // Default to black if neither black nor white satisfies the contrast ratio
      return black;
    }
  }

  loadCustomer$(): Observable<Customer> {
    return this.getCustomerConfigGQL.fetch({ customerKey: CustomerServiceImpl.GetCustomerKey() })
      .pipe(map(response => {
        const customer = response.data.getCustomer;
        if (customer.isInMaintenance) {
          this.router.navigate(['/maintenance'])
        }
        const config = response.data.getCustomer.config;

        // Primary color
        const primaryColor = config.colors?.primary
        const primaryTextColor = config.colors?.primaryText ? config.colors.primaryText : (primaryColor && this.calculateTextColor(primaryColor))

        // Upload panel
        const uploadPanelBackgroundColor = config.colors?.uploadPanelBackground
        const uploadPanelColor = config.colors?.uploadPanelText
          ? config.colors.uploadPanelText : (uploadPanelBackgroundColor && this.calculateTextColor(uploadPanelBackgroundColor))

        const imageButtonsBackground = config.colors?.imageButtonsBackground || config.colors?.primary
        const imageButtonsText = config.colors?.imageButtonsText
          ? config.colors.imageButtonsText : primaryTextColor

        const rgbColor = primaryColor ? this.hexToRgb(primaryColor) : undefined

        // Set the CSS variable
        if (primaryColor) {
          document.documentElement.style.setProperty('--customer-primary-color', primaryColor);
        }
        if (rgbColor) {
          document.documentElement.style.setProperty('--customer-primary-color-rgb', rgbColor);
        }

        if (primaryTextColor) {
          document.documentElement.style.setProperty('--customer-primary-text-color', primaryTextColor);
        }

        if (uploadPanelBackgroundColor) {
          document.documentElement.style.setProperty('--upload-panel-background-color', uploadPanelBackgroundColor);
        }
        if (uploadPanelColor) {
          document.documentElement.style.setProperty('--upload-panel-color', uploadPanelColor);
        }

        if (imageButtonsBackground) {
          document.documentElement.style.setProperty('--image-buttons-background-color', imageButtonsBackground);
        }

        if (imageButtonsText) {
          document.documentElement.style.setProperty('--image-buttons-text-color', imageButtonsText);
        }

        let supportedProductTypes = config.supportedProductTypes || undefined;
        // Make sure that door is always supported and at the beginning
        if (supportedProductTypes && !supportedProductTypes.includes('door')) {
          supportedProductTypes = ['door', ...supportedProductTypes];
        }

        return {
          name: response.data.getCustomer.name,
          homepageUrl: response.data.getCustomer.homepageUrl || undefined,
          config: {
            logoUrl: environment.serverUri + config.logoPath,
            defaultLanguage: config.defaultLanguage,
            translationFileUrl: config.translationsPath,
            isBrandingEnabled: config.showDoorVisionBranding,
            productLinkType: config.productLinkType,
            isFavoriteFeatureEnabled: config.useFavoritesList,
            drawGlassLayerInVisualisation: config.showGlassLayerInVisualization,
            enabledShadowProfiles: config.enabledShadowProfiles,
            enabledProducts: !!config.enabledProducts,
            allowMultipleDoors: !!config.allowMultipleDoors,
            allowClippedCorners: !!config.allowClippedCorners,
            enableShadowInpainting: !!config.enableShadowInpainting,
            shopLinkOverwrite: config.shopLinkOverwrite || undefined,
            enableDoorTypeDropdownSelection: !!config.enableDoorTypeDropdownSelection,
            mainDoorTypeFilter: config.mainDoorTypeFilter?.map(mapSmallGraphQLProductTypeToSmallProductType).filter(Boolean),
            supportedProductTypes,
            supportedDoorTypes: config.supportedDoorTypes?.map(mapSmallGraphQLProductTypeToSmallProductType).filter(Boolean),
            defaultDoorType: mapSmallGraphQLProductTypeToSmallProductType(config.defaultDoorType),
            showRefineLayoutAfterUpload: config.showRefineLayoutAfterUpload,
            enableVisualDoorsSearch: !!config.enableVisualDoorsSearch,

            enabledExampleImageSets: !!config.enabledExampleImageSets,
            gtmTag: config.gtmTag || undefined,

            hideShareButton: !!config.hideShareButton,
            hideDownloadButton: !!config.hideDownloadButton,
            showCustomAttributesFilter: !!config.showCustomAttributesFilter,
            hideFlipButton: !!config.hideFlipButton,
            hideRefineButtonUnderImage: !!config.hideRefineButtonUnderImage,
            showVisitHomepageButton: !!config.showVisitHomepageButton,
            alwaysShowImageButtons: !!config.alwaysShowImageButtons,
            logoPlacement: config.logoPlacement,
            imageButtonsArrangement: config.imageButtonsArrangement,
            imageButtonsPlacement: config.imageButtonsPlacement,

            colors: {
              primary: primaryColor || undefined,
              primaryText: primaryTextColor || undefined,
              primaryRgb: rgbColor || undefined,
              uploadPanel: {
                background: uploadPanelBackgroundColor || undefined,
                color: uploadPanelColor || undefined
              },
              imageButtons: {
                background: imageButtonsBackground || undefined,
                color: imageButtonsText || undefined
              }
            }
          }
        };
      }));
  }

  async getCustomer(): Promise<Customer | undefined> {
    return this.customer$.pipe(switchMap(value => value ? of(value) : EMPTY)).toPromise();
  }

  getCustomerHomepageUrl(): Observable<string> {
    // First try to load it from the customer directly
    return this.customer$.pipe(map(customer => {
      if (customer?.homepageUrl) {
        return customer.homepageUrl
      }

      // Fallback to our hardcoded list
      switch (CustomerServiceImpl.GetCustomerKey().toLowerCase()) {
        case 'deinetuer': return 'https://www.deinetuer.de'
        case 'directdoors': return 'https://www.directdoors.com'
        case 'siko': return 'https://www.siko.eu/'
        case 'tuerenshop': return 'https://tuerenshop.at/'
        case 'decodoorandcrown': return 'https://www.decodoorandcrown.com/'
        case 'ambiancedoors': return 'https://ambiancedoors.com/'
        case 'doorsuppliesonline': return 'https://www.doorsuppliesonline.co.uk/'
        default: return 'https://www.door-vision.com'
      }
    }))
  }
}
